import { FC, useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { EasTableWithoutFilter } from 'ui/EasTableWithoutFilter';
import { ESortDirection } from 'ui/EasTable/types';
import {
	clearPpwrEmPackagingTypes,
	searchPpwrEmPackagingTypesRequest,
} from 'redux/reducers/ppwr/reducer';
import {
	getPpwrEmPackagingTypes,
	getPpwrEmPackagingTypesLoading,
} from 'redux/reducers/ppwr/selectors';
import { TPpwrEmPackagingType } from 'redux/reducers/ppwr/types';
import { EDIT_ACTION, tableStructure } from './tableStructure';
import { EditWeightDialog } from './EditWeightDialog';

/** An EM has only a few packaging types, so the widget shows all of them on one page. */
const PACKAGING_TYPES_PAGE_SIZE = 1000;

type TPackingProfileProps = {
	emId: number;
};

/** "Packing profile" widget: the packaging types configured for the EM. */
export const PackingProfile: FC<TPackingProfileProps> = ({ emId }) => {
	const dispatch = useDispatch();
	const packagingTypes = useSelector(getPpwrEmPackagingTypes);
	const loading = useSelector(getPpwrEmPackagingTypesLoading);
	/** Index (within the loaded rows) of the row edited in the weight dialog. */
	const [editIndex, setEditIndex] = useState<number | null>(null);

	useEffect(() => {
		if (!Number.isFinite(emId)) return undefined;
		dispatch(
			searchPpwrEmPackagingTypesRequest({
				emId,
				body: {
					pageNumber: 1,
					pageSize: PACKAGING_TYPES_PAGE_SIZE,
					orderBy: [{ fieldName: 'pckgTypeName', direction: ESortDirection.ASC }],
				},
			}),
		);
		return () => {
			dispatch(clearPpwrEmPackagingTypes());
		};
	}, [dispatch, emId]);

	const handleAction = useCallback(
		(id: number, action: string) => {
			if (action !== EDIT_ACTION) return;
			const index = (packagingTypes?.data ?? []).findIndex((item) => item.id === id);
			if (index !== -1) setEditIndex(index);
		},
		[packagingTypes],
	);

	const handleDialogClose = useCallback(() => {
		setEditIndex(null);
	}, []);

	return (
		<>
			<EditWeightDialog
				rows={packagingTypes?.data ?? []}
				index={editIndex}
				setIndex={setEditIndex}
				onClose={handleDialogClose}
			/>
			<EasTableWithoutFilter<TPpwrEmPackagingType>
				tableStructure={tableStructure}
				items={packagingTypes}
				itemsIsLoading={loading}
				handleAction={handleAction}
				skeletonRows={[1, 2, 3]}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary" fontWeight="bold">
						Packing profile
					</Typography>
					{loading && <CircularProgress size={20} />}
				</Box>
			</EasTableWithoutFilter>
		</>
	);
};
